import { NonceAccount, PublicKey } from "@solana/web3.js";
import { SolanaStakingModule } from "./modules/solana-staking";
import { SDKConfiguration } from "./shared/configuration";
import { SolanaStakingError } from "./shared/error";
import { getLogger, maskSensitiveInUrl } from "./shared/logger";
/**
 * Main Solana Staking SDK class.
 *
 * @remarks
 * This is the primary entry point for all Solana staking operations.
 * It provides access to staking functionality through the solanaStaking module.
 *
 * The SDK follows the same patterns as the BD-SDK for consistency and
 * seamless integration with the Blockdaemon ecosystem.
 *
 * @example Basic Usage
 * ```typescript
 * import { SolanaStakingSdk } from "@blockdaemon/solana-staking-sdk";
 *
 * const sdk = new SolanaStakingSdk({
 *   rpcEndpoint: "https://api.mainnet-beta.solana.com",
 *   logLevel: "INFO"
 * });
 *
 * // Build a stake transaction
 * const votes = await sdk.getConnection().getVoteAccounts();
 * const validatorVotePubkey = votes.current[0]?.votePubkey ?? votes.delinquent[0]?.votePubkey;
 * const result = await sdk.solanaStaking.buildStakeTransaction({
 *   amount: 1_000_000_000,
 *   payerPubkey: "...",
 *   validatorVotePubkey,
 * });
 * ```
 *
 * @example With Custom Configuration
 * ```typescript
 * const sdk = new SolanaStakingSdk({
 *   rpcEndpoint: "https://api.devnet.solana.com",
 *   rpcAuth: "your-token",
 *   logLevel: "DEBUG",
 *   timeout: 60000,
 *   loggerOptions: {
 *     label: "my-solana-app",
 *     level: "debug"
 *   }
 * });
 * ```
 */
export class SolanaStakingSdk {
    logger = getLogger({ label: "SolanaStakingSdk" });
    config;
    /**
     * Solana staking module for all staking-related operations.
     *
     * @remarks
     * Provides access to:
     * - Validator querying and management
     * - Stake account creation and management
     * - Staking and unstaking transactions
     *
     * Note: Reward claiming and staking pool interactions are not yet implemented in this SDK.
     */
    solanaStaking;
    /**
     * Creates a new instance of the Solana Staking SDK.
     *
     * @param args - Configuration options for the SDK
     *
     * @example
     * ```typescript
     * const sdk = new SolanaStakingSdk({
     *   rpcEndpoint: "https://api.mainnet-beta.solana.com",
     *   logLevel: "INFO"
     * });
     * ```
     */
    constructor(args = {}) {
        this.logger.info("Initializing Solana Staking SDK");
        // Initialize configuration
        this.config = new SDKConfiguration({
            ...args.configuration,
            logLevel: args.logLevel || "INFO",
            timeout: args.timeout || 30000,
        });
        // Setup logger if custom options provided
        if (args.loggerOptions) {
            this.logger.info("Setting up custom logger configuration");
        }
        const authParam = args.external ? "apiKey" : "auth";
        const baseHttpEndpoint = args.rpcHttpEndpoint ?? "https://api.mainnet-beta.solana.com";
        const rpcHttpEndpoint = args.rpcHttpAuth
            ? `${baseHttpEndpoint}?${authParam}=${args.rpcHttpAuth}`
            : baseHttpEndpoint;
        const baseWsEndpoint = args.rpcWsEndpoint ?? ""; // Solana public node does not support ws
        const rpcWsEndpoint = args.rpcWsAuth
            ? `${baseWsEndpoint}?${authParam}=${args.rpcWsAuth}`
            : baseWsEndpoint;
        // Log a masked endpoint to avoid leaking secrets downstream
        this.logger.info("Configuring http RPC endpoint", {
            rpcHttpEndpoint: maskSensitiveInUrl(rpcHttpEndpoint),
        });
        if (rpcWsEndpoint !== "") {
            this.logger.info("Configuring ws RPC endpoint", {
                rpcWsEndpoint: maskSensitiveInUrl(rpcWsEndpoint),
            });
        }
        // Initialize solana staking module
        const stakingOptions = {
            rpcHttpEndpoint: rpcHttpEndpoint,
            rpcWsEndpoint: rpcWsEndpoint,
            timeout: args.timeout || 30000,
            configuration: this.config,
        };
        this.solanaStaking = new SolanaStakingModule(stakingOptions);
        this.logger.info("Solana Staking SDK initialized successfully");
    }
    /**
     * Gets the current SDK configuration.
     *
     * @returns The current SDK configuration object
     *
     * @example
     * ```typescript
     * const config = sdk.getConfiguration();
     * console.log("Current timeout:", config.timeout);
     * ```
     */
    getConfiguration() {
        return this.config;
    }
    /**
     * Gets the current SDK version.
     *
     * @returns The SDK version string
     *
     * @example
     * ```typescript
     * const version = sdk.getVersion();
     * console.log("SDK Version:", version);
     * ```
     */
    getVersion() {
        // This will be replaced with actual version from package.json
        return "0.0.1";
    }
    /**
     * Returns the underlying Solana `Connection` used by the staking module.
     *
     * @example
     * ```ts
     * const connection = sdk.getConnection();
     * const slot = await connection.getSlot();
     * ```
     */
    getConnection() {
        return this.solanaStaking.getConnection();
    }
    /**
     * Fetch the current durable nonce value for a given nonce account.
     *
     * @param nonceAccountPubkey - Nonce account public key (base58 string)
     * @param commitment - Optional commitment level (default: "finalized")
     * @returns The current nonce string stored in the nonce account
     */
    async getCurrentNonce(nonceAccountPubkey, commitment = "finalized") {
        const connection = this.getConnection();
        try {
            const pk = new PublicKey(nonceAccountPubkey);
            const info = await connection.getAccountInfo(pk, { commitment });
            if (!info?.data) {
                throw new SolanaStakingError(`Nonce account not found: ${nonceAccountPubkey}`);
            }
            const nonceData = NonceAccount.fromAccountData(info.data);
            return nonceData.nonce;
        }
        catch (error) {
            const message = `Failed to fetch current nonce: ${error instanceof Error ? error.message : String(error)}`;
            throw new SolanaStakingError(message, {
                originalError: error instanceof Error ? error : undefined,
            });
        }
    }
    /**
     * Performs a health check on the SDK and its connections.
     *
     * @returns Promise that resolves to health check results
     *
     * @example
     * ```typescript
     * const health = await sdk.healthCheck();
     * if (health.isHealthy) {
     *   console.log("SDK is healthy");
     * } else {
     *   console.log("SDK health issues:", health.issues);
     * }
     * ```
     */
    async healthCheck() {
        this.logger.info("Performing SDK health check");
        const issues = [];
        let solanaStakingHealthy = false;
        try {
            // Test solana staking module
            await this.solanaStaking.healthCheck();
            solanaStakingHealthy = true;
        }
        catch (error) {
            issues.push(`Solana staking module: ${error instanceof Error ? error.message : String(error)}`);
        }
        const isHealthy = issues.length === 0;
        const result = {
            isHealthy,
            version: this.getVersion(),
            timestamp: new Date(),
            modules: {
                solanaStaking: solanaStakingHealthy,
            },
            ...(issues.length > 0 && { issues }),
        };
        if (!isHealthy) {
            this.logger.info("Health check failed", { isHealthy, issueCount: issues.length });
        }
        return result;
    }
}
//# sourceMappingURL=data:application/json;base64,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